import { logError } from "@/lib/error-handling";
import { apiClient } from "@/services/api";
import type {
  MeetingPrepDto,
  RefreshTalkingPointsResponseDto,
  ReplaceTalkingPointsPayload,
  TalkingPointDto,
} from "@/types";

/** Appends one rep-authored point; the server always marks it USER_CREATED. */
export async function createTalkingPoint(
  prepId: string,
  content: string
): Promise<TalkingPointDto> {
  try {
    return await apiClient<TalkingPointDto>(
      `/api/preps/${prepId}/talking-points`,
      { method: "POST", body: JSON.stringify({ content }) }
    );
  } catch (error) {
    logError(error, "createTalkingPoint");
    throw error;
  }
}

/**
 * The live-checklist toggle. Returns the updated row, so the caller can splice
 * it into the cached prep instead of refetching the whole thing mid-call.
 */
export async function updateTalkingPoint(
  pointId: string,
  input: { checked: boolean }
): Promise<TalkingPointDto> {
  try {
    return await apiClient<TalkingPointDto>(`/api/talking-points/${pointId}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    });
  } catch (error) {
    logError(error, "updateTalkingPoint");
    throw error;
  }
}

/**
 * The Customize form's save: the complete list, in order, applied atomically.
 * Answers 409 when the list went stale, which the caller turns into a
 * reconciliation rather than an error message.
 */
export async function replaceTalkingPoints(
  prepId: string,
  payload: ReplaceTalkingPointsPayload
): Promise<MeetingPrepDto> {
  try {
    return await apiClient<MeetingPrepDto>(
      `/api/preps/${prepId}/talking-points`,
      { method: "PUT", body: JSON.stringify(payload) }
    );
  } catch (error) {
    logError(error, "replaceTalkingPoints");
    throw error;
  }
}

/**
 * The full ordered id list, never a slice. The endpoint rejects partial lists
 * on purpose, so a client with a stale view cannot drop points in silence.
 */
export async function reorderTalkingPoints(
  prepId: string,
  talkingPointIds: string[]
): Promise<{ talkingPoints: TalkingPointDto[] }> {
  try {
    return await apiClient<{ talkingPoints: TalkingPointDto[] }>(
      `/api/preps/${prepId}/talking-points/order`,
      { method: "PUT", body: JSON.stringify({ talkingPointIds }) }
    );
  } catch (error) {
    logError(error, "reorderTalkingPoints");
    throw error;
  }
}

/**
 * Asks the bg-agent for a regeneration. Always 202 — the response carries the
 * real status, which is the only way to tell a queued run from a no-op (the
 * request is idempotent, and generation may be disabled for the org).
 */
export async function refreshTalkingPoints(
  prepId: string
): Promise<RefreshTalkingPointsResponseDto> {
  try {
    return await apiClient<RefreshTalkingPointsResponseDto>(
      `/api/preps/${prepId}/refresh-talking-points`,
      { method: "POST" }
    );
  } catch (error) {
    logError(error, "refreshTalkingPoints");
    throw error;
  }
}
